import { FormField } from '@cosuno/cosuno-ui';
import { useField, useFormikContext } from 'formik';
import React, { useMemo } from 'react';

import FieldErrorScrollMarker from '~/shared/components/FieldErrorScrollMarker';
import { uniqueId } from '~/shared/utils/javascript';

export interface FieldProps {
  name: string;
  id?: string;
  className?: string;
  label?: string;
  required?: boolean;
  showAsRequired?: boolean;
  /**
   * This prop is used to control the width of the FormComponent inside the field wrapper.
   */
  wrapperWidth?: number;
  disabled?: boolean;
  invalid?: boolean;
  error?: React.ReactNode;
  errorTooltip?: boolean;
  showErrors?: boolean;
  showErrorsIfUntouched?: boolean;
  onChange?: (value: unknown) => void;
  onBlur?: (event: React.FocusEvent) => void;
  onBlurWithValue?: (value: unknown, values: unknown) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapFormValue?: (value: any) => any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mapChangedValue?: (value: any) => any;
  mapPercentage?: boolean;
  validate?: (value: unknown) => string | undefined;
  useMargins?: boolean;
  hint?: React.ReactNode;
}

type InjectedProps = 'value' | 'onChange';

export const generateField = <Props extends {}>(
  FormComponent: React.ComponentType<Props>,
  options?: {
    /**
     * Some inputs (i.e. radio buttons or selects) don't emit the blur event during normal use,
     * so they need to be marked as touched on change instead.
     */
    markAsTouchedOnChange?: boolean;
    /** Enable this in case input renders its own error message */
    hideErrors?: boolean;
  },
) => {
  const FieldComponent = React.forwardRef<unknown, FieldProps & Omit<Props, InjectedProps>>(
    (
      {
        className,
        id,
        name,
        label,
        required = false,
        showAsRequired = required,
        invalid,
        error: errorProp,
        errorTooltip,
        onChange = () => {},
        onBlur: onBlurProp,
        onBlurWithValue,
        mapFormValue: mapFormValueProp = (x) => x,
        mapChangedValue: mapChangedValueProp = (x) => x,
        mapPercentage,
        useMargins = true,
        showErrors: showErrorsProp = true,
        showErrorsIfUntouched = false,
        hint,
        validate,
        wrapperWidth,
        ...props
      },
      ref,
    ) => {
      const [{ value, ...inputProps }, fieldMetaProps, { setTouched }] = useField({
        name,
        validate,
        onBlur: onBlurProp,
      });
      const { setFieldValue, values } = useFormikContext();
      const fieldId = useMemo(() => id || uniqueId('field-'), [id]);
      const error = errorProp ?? fieldMetaProps.error;
      const hasError = (showErrorsIfUntouched || fieldMetaProps.touched) && error;
      const showErrors = !options?.hideErrors && showErrorsProp;

      const mapFormValue = mapPercentage
        ? (valueToMap?: string | number) =>
            valueToMap ? (Number(valueToMap) * 100).toFixed(3) : '0'
        : mapFormValueProp;
      const mapChangedValue = mapPercentage
        ? (valueToMap?: string | number) => `${Number(valueToMap) / 100}`
        : mapChangedValueProp;

      const handleBlur: React.EventHandler<React.FocusEvent<HTMLInputElement>> = (event) => {
        inputProps.onBlur(event);

        if (onBlurWithValue) {
          onBlurWithValue(mapChangedValue(event.target.value), values);
        }

        onBlurProp?.(event);
      };
      const handleChange = (newValue: unknown) => {
        if (options?.markAsTouchedOnChange) {
          void setTouched(true);
        }

        const mappedNewValue = mapChangedValue(newValue);

        void setFieldValue(name, mappedNewValue);
        onChange(mappedNewValue);
      };

      // TODO figure out where does invalid come from and fix it to boolean
      // `invalid` is not a boolean, but it has the error message instead
      const isInvalid = Boolean(invalid || hasError);

      return (
        <FormField
          className={className}
          label={label}
          labelHtmlFor={fieldId}
          disabled={props.disabled}
          useMargins={useMargins}
          showAsRequired={showAsRequired}
          exactWidth={wrapperWidth}
          hint={hint}
          error={showErrors && hasError ? error : undefined}
          errorTooltip={errorTooltip}
          data-cy-is-invalid={isInvalid}
          data-cy-invalid-message={error}
        >
          {error && <FieldErrorScrollMarker name={name} />}
          <FormComponent
            // Seems like we have to cast props as any, even though they should be a
            // subset of Props
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...(props as any)}
            {...inputProps}
            value={mapFormValue(value)}
            id={fieldId}
            invalid={isInvalid}
            required={required}
            onChange={handleChange}
            onBlur={handleBlur}
            ref={ref}
          />
        </FormField>
      );
    },
  );

  FieldComponent.displayName = `Field.${FormComponent.displayName || FormComponent.name}`;

  return FieldComponent;
};
